/**
 * 图标注册表 —— **全站唯一的图标来源**。
 *
 * ## 为什么要有这一层
 *
 * 之前图标直接从 `@element-plus/icons-vue` 散着 import。两个后果：
 *
 * 1. EP 是实心填充风格，Lucide 是 1.5px 描边风格。同一屏里混着用，
 *    「刷新」按钮是实心的、侧栏是描边的，看起来像两个产品拼起来的。
 * 2. 换图标要全仓库搜替换。集中在这里之后，换一个图标只改一行。
 *
 * ## 命名沿用 Lucide 自己的名字
 *
 * 不保留 EP 的旧名（`Refresh`、`InfoFilled`…）。旧名会说谎 ——
 * Lucide 的 `Info` 是描边的，叫它 `InfoFilled` 只会让下一个人以为还有个描边版。
 *
 * ## 尺寸约定
 *
 * Lucide 组件默认渲染 24×24。项目里一律用 Tailwind 的 `size-*` 覆盖：
 * 行内文字旁 `size-4`，导航 `size-[18px]`，空态大图 `size-10`。
 * 不要用 `<el-icon>` 包一层 —— 那是 EP 的字号继承机制，对 Lucide 没有意义。
 */
import type { Component } from 'vue'
import {
  Activity,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  ArrowUpDown,
  AudioLines,
  AudioWaveform,
  Blocks,
  BookOpen,
  BookOpenText,
  Bot,
  Box,
  Boxes,
  Braces,
  BrainCircuit,
  Building2,
  Calculator,
  ChartColumn,
  ChartLine,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  CircleHelp,
  CircleMinus,
  CirclePlus,
  ClipboardCheck,
  ClipboardList,
  Clock,
  CloudDownload,
  CloudUpload,
  Code,
  Copy,
  CopyPlus,
  Cpu,
  Database,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  Files,
  FileSpreadsheet,
  FileText,
  Fingerprint,
  FlaskConical,
  FolderOpen,
  Gauge,
  Gavel,
  GitBranch,
  GitCompare,
  GitCompareArrows,
  GitMerge,
  Globe,
  Hand,
  HardDriveDownload,
  History,
  Image as ImageIcon,
  Info,
  Key,
  KeyRound,
  LayoutDashboard,
  LayoutGrid,
  Library,
  Link,
  List,
  ListChecks,
  ListOrdered,
  ListTree,
  LoaderCircle,
  Lock,
  LockOpen,
  LogOut,
  Maximize,
  Maximize2,
  MessageCircle,
  MessageSquareQuote,
  MessageSquareText,
  MessagesSquare,
  Mic,
  Minimize,
  Minimize2,
  Minus,
  Monitor,
  Moon,
  MoreHorizontal,
  MousePointer2,
  Network,
  PackagePlus,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelsTopLeft,
  Paperclip,
  Pause,
  Pencil,
  Pin,
  PinOff,
  Play,
  Plug,
  PlugZap,
  Plus,
  Power,
  PowerOff,
  Presentation,
  Redo2,
  RefreshCw,
  Repeat,
  Rocket,
  RotateCcw,
  RotateCw,
  Save,
  ScrollText,
  Search,
  Send,
  Server,
  ServerCog,
  ServerOff,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Split,
  Stethoscope,
  Square,
  Star,
  Store,
  Sun,
  Table,
  Ticket,
  Trash2,
  TriangleAlert,
  Undo2,
  Unlink,
  Upload,
  User,
  UserRound,
  Users,
  UserX,
  Variable,
  Volume2,
  Wand,
  Waypoints,
  Workflow,
  Wrench,
  X,
} from 'lucide-vue-next'

export {
  Activity,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  ArrowUpDown,
  AudioLines,
  AudioWaveform,
  Blocks,
  BookOpen,
  BookOpenText,
  Bot,
  Box,
  Boxes,
  BrainCircuit,
  Building2,
  Calculator,
  Braces,
  ChartColumn,
  ChartLine,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  CircleHelp,
  CircleMinus,
  CirclePlus,
  ClipboardCheck,
  ClipboardList,
  Clock,
  CloudDownload,
  CloudUpload,
  Code,
  Copy,
  CopyPlus,
  Cpu,
  Database,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  Files,
  FileSpreadsheet,
  FileText,
  FlaskConical,
  FolderOpen,
  Gauge,
  Gavel,
  GitBranch,
  GitCompare,
  GitCompareArrows,
  GitMerge,
  Globe,
  Hand,
  HardDriveDownload,
  History,
  ImageIcon,
  Info,
  Key,
  KeyRound,
  LayoutDashboard,
  LayoutGrid,
  Library,
  Link,
  List,
  ListChecks,
  ListOrdered,
  ListTree,
  LoaderCircle,
  Lock,
  LockOpen,
  LogOut,
  Maximize,
  Maximize2,
  MessageCircle,
  MessageSquareQuote,
  MessageSquareText,
  MessagesSquare,
  Mic,
  Minimize,
  Minimize2,
  Minus,
  Monitor,
  Moon,
  MoreHorizontal,
  MousePointer2,
  Network,
  PackagePlus,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelsTopLeft,
  Paperclip,
  Pause,
  Pencil,
  Pin,
  PinOff,
  Play,
  Plug,
  PlugZap,
  Plus,
  Power,
  PowerOff,
  Presentation,
  Redo2,
  RefreshCw,
  Repeat,
  Rocket,
  RotateCcw,
  RotateCw,
  Save,
  ScrollText,
  Search,
  Send,
  Server,
  ServerCog,
  ServerOff,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Split,
  Square,
  Star,
  Store,
  Sun,
  Table,
  Ticket,
  Trash2,
  TriangleAlert,
  Undo2,
  Unlink,
  Upload,
  User,
  UserRound,
  Users,
  UserX,
  Variable,
  Volume2,
  Wand,
  Workflow,
  Wrench,
  X,
}

/**
 * 按名字取图标 —— 给**运行时才知道名字**的地方用：
 * 路由表的 `meta.icon`、`MENU_GROUPS[].icon`、`MetricCard` 的 `icon` prop。
 *
 * 只登记这几处真正用到的，不是 Lucide 全集。全集有 1500 个图标，
 * 全登记会让打包器没法摇掉没用到的那些。
 */
const REGISTRY: Readonly<Record<string, Component>> = {
  Activity,
  AudioLines,
  AudioWaveform,
  BookOpen,
  BookOpenText,
  Blocks,
  Bot,
  Box,
  BrainCircuit,
  Building2,
  Calculator,
  ChartColumn,
  ChartLine,
  CirclePlus,
  ClipboardCheck,
  ClipboardList,
  Clock,
  CloudDownload,
  CloudUpload,
  Cpu,
  Database,
  Files,
  Fingerprint,
  FlaskConical,
  FolderOpen,
  Gauge,
  GitBranch,
  GitCompareArrows,
  GitMerge,
  Globe,
  History,
  KeyRound,
  Library,
  LayoutDashboard,
  LayoutGrid,
  List,
  ListChecks,
  ListOrdered,
  ListTree,
  MessageCircle,
  MessageSquareText,
  MessagesSquare,
  Monitor,
  MoreHorizontal,
  Network,
  PanelRightClose,
  PanelsTopLeft,
  Plug,
  PlugZap,
  Rocket,
  ScrollText,
  Server,
  ServerCog,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Stethoscope,
  Store,
  Users,
  Waypoints,
  Workflow,
  Wrench,
}

/**
 * 名字取不到时**在开发期喊出来**。
 *
 * 不能直接 `return Icons[name]`：取不到就是 `undefined`，而模板里
 * `<component :is="undefined">` 什么都不渲染 —— 图标静悄悄地没了，而侧栏少一个图标
 * 没人会当成 bug 报。名字写错一个字母就是这个下场。
 */
export function iconByName(name?: string): Component | undefined {
  if (!name) return undefined
  const icon = REGISTRY[name]
  if (!icon && import.meta.env.DEV) {
    console.warn(
      `[icons] 「${name}」不在注册表里。图标会缺失，请在 icons.ts 的 REGISTRY 里补上。`,
    )
  }
  return icon
}

/**
 * 可供运行时选择的图标名，按字母序。菜单管理的图标选择器消费它。
 *
 * 与 `iconByName` 同源：往 REGISTRY 里加一个图标，选择器自动多一个候选，
 * 不存在第二份会分叉的名单。
 */
export const ICON_NAMES: readonly string[] = Object.keys(REGISTRY).sort()
